import { useMemo } from 'react';
import { isNil } from 'lodash';
import { ACTIVITY_TYPES } from '../../../constants/activity';
import { isAdministrator } from '../../../utilities/user';
import { assetUploadOriginEnum } from '@larki/shared/enums';
import { getCreatorAsMember } from '../QuoteActivityTemplates/utils';
import { JobT } from '@larki/shared/types/job.type';
import { UserT } from '@larki/shared/types/user.type';
import type { ActivityMetadata } from '@larki/shared/types/activity.type';

type CurrentUser = {
  profile?: { id?: number; role?: string | null } | null;
  isStaff?: boolean;
  isEssentialUser?: boolean;
  isGovernment?: boolean;
};

// File-type activities carry asset-shaped metadata (with `uploadOrigin`),
// not the order-activity discriminated union.
type ActivityHookMetadata =
  | ActivityMetadata
  | { uploadOrigin?: string | null; variant?: undefined };

const isActivityMetadata = (
  m: ActivityHookMetadata | null | undefined
): m is ActivityMetadata => !!m && 'variant' in m && !!m.variant;

type Args = {
  currentUser: CurrentUser;
  metadata: ActivityHookMetadata | null | undefined;
  job: JobT | undefined;
  type: string;
  createdAssets: unknown[];
  supersededAssets: unknown[];
  createdUser: UserT;
  projectId: number | null | undefined;
};

export function useActivityMenuVisibility({
  currentUser,
  metadata,
  job,
  type,
  createdAssets,
  supersededAssets,
  createdUser,
  projectId,
}: Args) {
  const isOrderActivity = !!(
    job &&
    type === ACTIVITY_TYPES.order &&
    isActivityMetadata(metadata)
  );
  const activityMetadata = useMemo(
    () => (isOrderActivity && isActivityMetadata(metadata) ? metadata : null),
    [isOrderActivity, metadata]
  );

  const isSuperseded = supersededAssets.length > 0;
  const isDeleteVisible =
    isAdministrator(currentUser.profile?.role) && !isNil(projectId);
  const isSupersedeVisible = !!(createdAssets && createdAssets.length > 0);
  const isHideVisible = !!currentUser.isStaff && !isNil(projectId);
  const isRfqActivity = metadata?.variant === 'request_for_quote';
  const isEditPriceVisible =
    !!currentUser.isStaff && isRfqActivity && !isNil(projectId) && !!job?.id;
  const creatorIsEssentialUser = getCreatorAsMember(createdUser, currentUser);
  const isUploadOriginFromPaymentFlow =
    !!metadata &&
    'uploadOrigin' in metadata &&
    metadata.uploadOrigin === assetUploadOriginEnum.enum.payment_flow;

  return {
    isOrderActivity,
    activityMetadata,
    isSuperseded,
    isDeleteVisible,
    isSupersedeVisible,
    isHideVisible,
    isEditPriceVisible,
    creatorIsEssentialUser,
    isUploadOriginFromPaymentFlow,
  };
}
